import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSnowflake,
  faTemperatureArrowDown,
  faJar,
  faArrowLeft,
  faShoppingCart,
  faLightbulb,
  faHeart as faHeartSolid,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import FsLightbox from "fslightbox-react";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCart } from "../../hooks/useCart";
import { useFavorites } from "../../hooks/useFavorites";
import getAuthHeaders from "../../utils/getAuthHeaders";
import reportError from "../../utils/reportError";
import Loading from "../Loading";

//styles
import "./ProductDetails.css";

export default function ProductDetails() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { cart, updateCart } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxToggler, setLightboxToggler] = useState(false);
  const [slideNumber, setSlideNumber] = useState(1);
  const [showDateInfo, setShowDateInfo] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const authHeader = await getAuthHeaders(user);

        let productUrl = `/api/products/${productId}`;

        if (process.env.NODE_ENV === "development") {
          productUrl = `${process.env.REACT_APP_NETWORK}${productUrl}`;
        }

        const response = await fetch(productUrl, { headers: authHeader });

        if (!response.ok) {
          throw new Error("Product not found");
        }

        const json = await response.json();
        setProduct(json);
        setLoading(false);
      } catch (err) {
        reportError(err, "fetch product details");
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, user]);

  // Auto-hide date info after 3 seconds
  useEffect(() => {
    if (showDateInfo) {
      const timer = setTimeout(() => {
        setShowDateInfo(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showDateInfo]);

  const openLightboxOnSlide = (number) => {
    setSlideNumber(number);
    setLightboxToggler((prev) => !prev);
  };

  const getTemperatureIcon = (temp) => {
    switch (temp) {
      case "F":
        return faSnowflake;
      case "C":
        return faTemperatureArrowDown;
      case "D":
        return faJar;
      default:
        return null;
    }
  };

  const getTemperatureText = (temp) => {
    switch (temp) {
      case "F":
        return "Frozen";
      case "C":
        return "Refrigerated";
      case "D":
        return "Dry";
      default:
        return "";
    }
  };

  const formatPrice = (price) => {
    return `$${(price / 100).toFixed(2)}`;
  };

  const getDateDescription = (dateCode) => {
    const descriptions = {
      BB: "BB = Best By Date",
      UB: "UB = Use By Date",
      EX: "EX = Expiration Date",
      FB: "FB = Freeze By Date",
      PD: "PD = Production Date",
      SB: "SB = Sell By Date",
      CS: "CS = Date on Case Only",
      JD: "JD = Julian Date",
    };
    return descriptions[dateCode] || "";
  };

  const handleAddToCart = () => {
    updateCart("ADD_ITEM", product);
  };

  const handleRemoveFromCart = () => {
    updateCart("REMOVE_ITEM", product);
  };

  const getCartQuantity = () => {
    return cart?.find((x) => x.productId === product.productId)?.quantity ?? 0;
  };

  const getImageSources = () => {
    // Ensure we always return a flat array of strings
    if (
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      return product.images;
    }
    // Fallback to single image if available
    if (product.image) {
      // If product.image is already an array, use it; otherwise wrap it
      return Array.isArray(product.image) ? product.image : [product.image];
    }
    // Fallback to thumbnail if nothing else available
    if (product.thumbnailUrl) {
      return [product.thumbnailUrl];
    }
    return [];
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !product) {
    return (
      <div className="product-details-error">
        <h3>Product not found</h3>
        <button onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Go Back
        </button>
      </div>
    );
  }

  const quantity = getCartQuantity();
  const imageSources = getImageSources();

  return (
    <div className="product-details-container">
      <div className="product-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
      </div>

      <div className="product-details-content">
        {/* Main Image Section */}
        <div className="product-details-images">
          <div className="main-image-container">
            <img
              src={
                imageSources[0] ||
                product.thumbnailUrl ||
                product.images?.[0] ||
                product.image
              }
              alt={product.description}
              onClick={() => openLightboxOnSlide(1)}
              className="main-image"
            />
          </div>

          {/* Additional Images Thumbnails */}
          {imageSources.length > 1 && (
            <div className="thumbnail-gallery">
              {imageSources.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`${product.description} - view ${index + 1}`}
                  onClick={() => openLightboxOnSlide(index + 1)}
                  className="thumbnail"
                />
              ))}
            </div>
          )}

          {imageSources.length > 0 && (
            <FsLightbox
              toggler={lightboxToggler}
              sources={imageSources}
              types={imageSources.map(() => "image")}
              slide={slideNumber}
              key={productId}
            />
          )}
        </div>

        {/* Product Information Section */}
        <div className="product-details-info">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h1 className="product-title">
              {product.description}, {product.unitSize}
            </h1>
            <FontAwesomeIcon
              icon={
                isFavorite(product.productId) ? faHeartSolid : faHeartRegular
              }
              onClick={() => toggleFavorite(product.productId)}
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
                color: isFavorite(product.productId) ? "#359356" : "#359356",
                marginLeft: "15px",
                flexShrink: 0,
              }}
            />
          </div>

          {/* Temperature Badge */}
          {product.temperature && (
            <div className="temperature-badge">
              <FontAwesomeIcon
                icon={getTemperatureIcon(product.temperature)}
                className="temperature-icon"
              />
              <span>{getTemperatureText(product.temperature)}</span>
            </div>
          )}

          {/* Pricing */}
          <div className="product-pricing">
            <div className="price-item">
              <span className="price-label">Case Price:</span>
              <span className="price-value">
                {formatPrice(product.casePrice)}
              </span>
            </div>
            <p className="price-per-unit">
              ({formatPrice(product.unitPrice)} /{" "}
              {product.unitLabel === "lb." ? "lb." : "item"}
              {product.pricePerUnit &&
                product.unitLabel !== "lb." &&
                ` | ${product.pricePerUnit}`}
              )
            </p>
          </div>

          {/* Add to Cart Section */}
          <div className="product-cart-actions">
            {quantity === 0 ? (
              <button
                className="add-to-cart-button"
                onClick={handleAddToCart}
                disabled={product.quantityAvailable === 0}
              >
                <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
              </button>
            ) : (
              <div className="cart-quantity-controls">
                <button
                  onClick={handleRemoveFromCart}
                  className="quantity-button"
                >
                  -
                </button>
                <span className="quantity-display">{quantity} added</span>
                <button
                  onClick={handleAddToCart}
                  className="quantity-button"
                  disabled={quantity >= product.quantityAvailable}
                >
                  +
                </button>
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="product-info-grid">
            {product.date && (
              <div className="info-item">
                <span className="info-label">Best By:</span>
                <span className="info-value">
                  {product.date}
                  <FontAwesomeIcon
                    icon={faLightbulb}
                    style={{
                      marginLeft: "6px",
                      fontSize: "0.85em",
                      color: "rgba(150, 150, 150, 0.3)",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDateInfo(!showDateInfo);
                    }}
                  />
                  {showDateInfo && (
                    <span
                      style={{
                        display: "block",
                        fontSize: "0.85em",
                        color: "#666",
                        fontStyle: "italic",
                        marginTop: "4px",
                      }}
                    >
                      {getDateDescription(product.date.slice(0, 2))}
                    </span>
                  )}
                </span>
              </div>
            )}
            {product.quantityAvailable !== undefined && (
              <div className="info-item">
                <span className="info-label"> Cases Available:</span>
                <span className="info-value">
                  {product.quantityAvailable > 0
                    ? `${product.quantityAvailable} in stock`
                    : "Out of stock"}
                </span>
              </div>
            )}
            {product.parentCategory && (
              <div className="info-item">
                <span className="info-label">Category:</span>
                <span className="info-value">{product.parentCategory}</span>
              </div>
            )}
            {product.subCategory && (
              <div className="info-item">
                <span className="info-label">Sub-Category:</span>
                <span className="info-value">{product.subCategory}</span>
              </div>
            )}
            {product.upc && (
              <div className="info-item">
                <span className="info-label">UPC:</span>
                <span className="info-value">{product.upc}</span>
              </div>
            )}
            {product.multipack && (
              <div className="info-item">
                <span className="info-label">GTIN:</span>
                <span className="info-value">{product.multipack}</span>
              </div>
            )}
            {product.productId && (
              <div className="info-item">
                <span className="info-label">Product ID:</span>
                <span className="info-value">{product.productId}</span>
              </div>
            )}
          </div>

          {/* Ingredients Section */}
          {product.sherlock && product.sherlock.ingredients && (
            <div className="product-ingredients">
              <h3>Ingredients</h3>
              <p className="ingredients-text">{product.sherlock.ingredients}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
